import { Schema } from "effect";
import {
  ChannelThreadId,
  MessageId,
  SupportTicketId,
  TopicId,
} from "../../../ids/definitions";
import { CustomerBasicInfo } from "../../customer";
import { MessageChannel, MessageSenderType } from "./message";
import { Attachment } from "./message/attachment";
import { MessageSmsService } from "./message/sms";
import {
  SupportTicketAssigneeType,
  SupportTicketStatus,
} from "./support-ticket";

/** Only non-closed handoff targets are surfaced here. */
export const TextConciergeEscalatedSupportTicket = Schema.Struct({
  id: SupportTicketId,
  status: SupportTicketStatus,
});
export type TextConciergeEscalatedSupportTicket =
  typeof TextConciergeEscalatedSupportTicket.Type;

/**
 * One row in the concierge conversations list. Backed by a
 * `shopper_agent_conversation` row joined with its channel thread's messages
 * (counts + timestamps) and any open handoff to a support ticket.
 */
export const ShopperAgentConversationListItem = Schema.Struct({
  topicId: TopicId,
  channelThreadId: ChannelThreadId,
  channel: MessageChannel,
  customer: Schema.NullOr(CustomerBasicInfo),
  numCustomerMessages: Schema.Number,
  numMerchantMessages: Schema.Number,
  lastCustomerMessageAt: Schema.NullOr(Schema.Date),
  lastMerchantMessageAt: Schema.NullOr(Schema.Date),
  lastCustomerMessageContent: Schema.NullOr(Schema.String),
  escalatedSupportTicket: Schema.NullOr(TextConciergeEscalatedSupportTicket),
  createdAt: Schema.Date,
  updatedAt: Schema.Date,
});
export type ShopperAgentConversationListItem =
  typeof ShopperAgentConversationListItem.Type;

export const ShopperAgentConversationListResponse = Schema.Struct({
  items: Schema.Array(ShopperAgentConversationListItem),
  hasMore: Schema.Boolean,
});
export type ShopperAgentConversationListResponse =
  typeof ShopperAgentConversationListResponse.Type;

export const TextConciergeFilterTab = Schema.Literal(
  "all",
  "awaiting",
  "replied",
  "escalated",
);
export type TextConciergeFilterTab = typeof TextConciergeFilterTab.Type;

/**
 * Slim message projection — bubble content, direction, timestamp, and
 * iMessage-vs-SMS styling cue only, so callers don't have to assemble the
 * full channel-specific Message union.
 */
export const TextConciergeMessage = Schema.Struct({
  id: MessageId,
  channelThreadId: ChannelThreadId,
  channel: MessageChannel,
  senderType: MessageSenderType,
  content: Schema.NullOr(Schema.String),
  sentAt: Schema.Date,
  // SMS-lane service; "iMessage" drives the blue (vs. green) preview bubble.
  messageService: Schema.optional(Schema.NullOr(MessageSmsService)),
  // MMS media and other files on the message, with resolved (presigned where
  // private) URLs. Optional on the wire so older payloads decode cleanly.
  attachments: Schema.optionalWith(Schema.Array(Attachment), {
    default: () => [],
  }),
});
export type TextConciergeMessage = typeof TextConciergeMessage.Type;

/**
 * `supportTicketId` and `assigneeType` are null until the conversation is
 * escalated to a shopper_support topic.
 */
export const ShopperAgentConversationDetailResponse = Schema.Struct({
  topicId: TopicId,
  channelThreadId: ChannelThreadId,
  channel: MessageChannel,
  customer: Schema.NullOr(CustomerBasicInfo),
  messages: Schema.Array(TextConciergeMessage),
  supportTicketId: Schema.NullOr(SupportTicketId),
  assigneeType: Schema.NullOr(SupportTicketAssigneeType),
});
export type ShopperAgentConversationDetailResponse =
  typeof ShopperAgentConversationDetailResponse.Type;
